/**
 * 短信验证API函数
 * 提供短信验证码相关功能
 */
import { supabase } from '../supabaseClient';

/**
 * 创建新的验证码
 * @param {string} phone - 手机号码
 * @returns {Promise<Object>} 创建结果和验证码
 */
export const createVerificationCode = async (phone) => {
  try {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      return { success: false, error: '无效的手机号码' };
    }
    
    // 调用Edge Function发送验证码
    const { data, error: functionError } = await supabase.functions.invoke('send-sms-code', {
      body: { phone }
    });

    if (functionError) throw functionError;
    if (!data.success) throw new Error(data.message || '验证码发送失败');
    
    return { 
      success: true, 
      message: data.message || '验证码已发送',
      expiresAt: data.expiresAt
    };
  } catch (error) {
    console.error('创建验证码失败:', error);
    return { success: false, error: error.message || '创建验证码失败' };
  }
};

/**
 * 验证短信验证码
 * @param {string} phone - 手机号码
 * @param {string} code - 验证码
 * @returns {Promise<Object>} 验证结果
 */
export const verifyCode = async (phone, code) => {
  try {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      return { success: false, error: '无效的手机号码' };
    }
    
    if (!code) {
      return { success: false, error: '验证码不能为空' };
    }
    
    // 调用Edge Function验证验证码
    const { data, error: functionError } = await supabase.functions.invoke('verify-sms-code', {
      body: { phone, code }
    });

    if (functionError) throw functionError;
    if (!data.success) throw new Error(data.message || '验证码无效或已过期');
    
    return { success: true, data: data };
  } catch (error) {
    console.error('验证码验证失败:', error);
    return { success: false, error: error.message || '验证码验证失败' };
  }
};

/**
 * 将验证码标记为已使用
 * @param {number} verificationId - 验证码记录ID
 * @returns {Promise<Object>} 操作结果
 */
export const markCodeAsUsed = async (verificationId) => {
  try {
    if (!verificationId) {
      return { success: false, error: '验证码ID不能为空' };
    }
    
    // 直接查询数据库表将验证码标记为已使用
    const { data, error } = await supabase
      .from('sms_verification')
      .update({ used: true, updated_at: new Date() })
      .eq('id', verificationId);

    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error('标记验证码为已使用失败:', error);
    return { success: false, error: error.message || '标记验证码失败' };
  }
};

/**
 * 检查验证码是否已存在且有效
 * @param {string} phone - 手机号码
 * @returns {Promise<Object>} 检查结果和剩余时间
 */
export const checkExistingCode = async (phone) => {
  try {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      return { success: false, error: '无效的手机号码' };
    }
    
    // 直接查询数据库表检查验证码状态
    const now = new Date();
    const { data, error } = await supabase
      .from('sms_verification')
      .select('*')
      .eq('phone', phone)
      .eq('used', false)
      .gte('expires_at', now.toISOString())
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    
    const hasExistingCode = data && data.length > 0;
    let cooldownRemaining = 0;
    let expiresIn = 0;
    
    if (hasExistingCode) {
      const code = data[0];
      // 计算过期时间(毫秒)
      const expiresAt = new Date(code.expires_at);
      expiresIn = Math.max(0, Math.floor((expiresAt - now) / 1000));
      
      // 计算冷却时间(毫秒)，假设冷却时间为60秒
      const createdAt = new Date(code.created_at);
      const cooldownTime = 60; // 60秒冷却时间
      const timeSinceCreation = Math.floor((now - createdAt) / 1000);
      cooldownRemaining = Math.max(0, cooldownTime - timeSinceCreation);
    }
    
    return { 
      success: true, 
      hasExistingCode,
      cooldownRemaining,
      expiresIn
    };
  } catch (error) {
    console.error('检查验证码失败:', error);
    return { success: false, error: error.message || '检查验证码失败' };
  }
};

/**
 * 通过Edge Function发送短信验证码
 * @param {string} phone - 手机号码
 * @returns {Promise<Object>} 发送结果
 */
export const sendSmsCodeViaFunction = async (phone) => {
  // 直接调用createVerificationCode函数，它已经使用Edge Function
  return await createVerificationCode(phone);
}; 